/**
 * Image optimization utilities for lazy loading and responsive images
 */

/**
 * Creates a responsive srcset string for different image sizes
 * @param {string} basePath - Base path to the image directory
 * @param {string} filename - Image filename without extension
 * @param {Array} sizes - Array of size suffixes
 * @returns {string} - Formatted srcset string
 */
export function createSrcSet(
  basePath,
  filename,
  sizes = ["thumbnail", "small", "medium", "large", "xlarge"]
) {
  const sizeMap = {
    thumbnail: 150,
    small: 300,
    medium: 600,
    large: 900,
    xlarge: 1200,
  };

  return sizes
    .map((size) => {
      const src = `${basePath}/${filename}-${size}.webp`;
      const width = sizeMap[size] || 600;
      return `${src} ${width}w`;
    })
    .join(", ");
}

/**
 * Gets the appropriate image size based on viewport width
 * @param {number} viewportWidth - Current viewport width
 * @returns {string} - Size suffix to use
 */
export function getOptimalImageSize(viewportWidth) {
  if (viewportWidth <= 640) return "small";
  if (viewportWidth <= 1024) return "medium";
  if (viewportWidth <= 1440) return "large";
  return "xlarge";
}

/**
 * Creates an Intersection Observer for lazy loading
 * @param {Function} callback - Function to call when element intersects
 * @param {Object} options - Observer options
 * @returns {IntersectionObserver} - Configured observer
 */
export function createLazyLoadObserver(callback, options = {}) {
  const defaultOptions = {
    rootMargin: "50px",
    threshold: 0.1,
    ...options,
  };

  if (!("IntersectionObserver" in window)) {
    // Fallback for older browsers
    return {
      observe: () => callback(),
      disconnect: () => {},
    };
  }

  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback(entry);
      }
    });
  }, defaultOptions);
}

/**
 * Preloads an image and returns a promise
 * @param {string} src - Image source URL
 * @returns {Promise} - Promise that resolves when image loads
 */
export function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Creates a blur-up effect by loading low quality first, then high quality
 * @param {string} lowQualitySrc - Low quality image source
 * @param {string} highQualitySrc - High quality image source
 * @param {Function} onLowQualityLoad - Callback when low quality loads
 * @param {Function} onHighQualityLoad - Callback when high quality loads
 */
export function createBlurUpEffect(
  lowQualitySrc,
  highQualitySrc,
  onLowQualityLoad,
  onHighQualityLoad
) {
  // Load low quality first
  preloadImage(lowQualitySrc)
    .then(() => {
      onLowQualityLoad();
      // Then load high quality
      return preloadImage(highQualitySrc);
    })
    .then(() => {
      onHighQualityLoad();
    })
    .catch((error) => {
      console.error("Failed to load images for blur-up effect:", error);
      // Fallback to high quality only
      preloadImage(highQualitySrc).then(onHighQualityLoad).catch(console.error);
    });
}

/**
 * Generates a data URL for a skeleton placeholder
 * @param {number} width - Placeholder width
 * @param {number} height - Placeholder height
 * @param {string} color - Placeholder color
 * @returns {string} - Data URL for the placeholder
 */
export function generateSkeletonPlaceholder(
  width = 400,
  height = 300,
  color = "#f0f0f0"
) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // Fill background
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);

  // Add subtle pattern
  ctx.fillStyle = "#e0e0e0";
  for (let i = 0; i < width; i += 20) {
    for (let j = 0; j < height; j += 20) {
      if ((i + j) % 40 === 0) {
        ctx.fillRect(i, j, 10, 10);
      }
    }
  }

  return canvas.toDataURL();
}

/**
 * Debounces a function to prevent excessive calls during scroll/resize
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
